import { css } from '@emotion/core'

// 部品をまたぐ入れ子のセレクタで書かれているため、部品ごとに分けず、紙の一番外側に1つだけ付ける
// 寸法は、Figma の紙（幅 361px）の値を 576/361 倍したもの
export const receiptSheetStyle = css({
  // 用紙サイズ 80mm=576px
  width: 576,
  paddingBottom: 38.3,
  lineHeight: 1.5,
  // src/index.tsx の `*` が要素ごとに書体と字間を指定するため、継承に任せず紙の中のすべての要素に指定し直す
  '&, & *': {
    fontFamily: "'Hiragino Kaku Gothic ProN', 'Hiragino Sans', sans-serif",
    fontWeight: 600,
    color: '#000000',
    letterSpacing: 0,
  },
  '.logo-block': {
    padding: '12.8px 0',
    img: {
      display: 'block',
      width: 354.2,
      height: 132.4,
      margin: '0 auto',
    },
  },
  '.accommodation-block': {
    padding: '0 51.1px',
    h2: {
      padding: '51.1px 0 25.5px',
      fontSize: 33.5,
    },
    '.room-block': {
      paddingTop: 25.5,
      '.room-row': {
        display: 'flex',
        alignItems: 'center',
        '.label': {
          width: 150,
          minWidth: 150,
          fontSize: 22.3,
        },
        '.value': {
          fontSize: 33.5,
          // 行の送り（51.1px）を値の行の高さにして、行の間を空けない
          lineHeight: '51.1px',
        },
      },
      hr: { marginTop: 25.5 },
      '.qr-code-block': {
        display: 'flex',
        justifyContent: 'center',
        paddingTop: 25.5,
      },
      '.qrcode-description': {
        paddingTop: 25.5,
        fontSize: 19.1,
        lineHeight: '28.7px',
      },
      '.card-replacement-notice': {
        paddingTop: 25.5,
        fontSize: 19.1,
        lineHeight: '28.7px',
      },
    },
    '.reservation-block': {
      padding: '12.8px 0',
    },
    '.comment': {
      paddingTop: 25.5,
      fontSize: 19.1,
      lineHeight: '28.7px',
      whiteSpace: 'break-spaces',
    },
  },
  '.payment-card-block': {
    padding: '0 51.1px',
    '.stats-block': {
      paddingTop: 12.8,
      fontSize: 19.1,
      lineHeight: '28.7px',
    },
    '.detail-block': {
      paddingTop: 25.5,
      '.list-item-block': {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: 22.3,
        '.label': { width: 100, minWidth: 100 },
      },
      hr: { margin: '12.8px 0' },
    },
    '.breakdown-block': {
      textAlign: 'right',
      fontSize: 19.1,
      lineHeight: '28.7px',
    },
    '.note-text': {
      paddingTop: 12.8,
      fontSize: 19.1,
      lineHeight: '28.7px',
    },
    '.issuer-block': {
      display: 'flex',
      alignItems: 'center',
      gap: 22.3,
      paddingTop: 12.8,
      '.hotel-address': {
        flexGrow: 1,
        fontSize: 19.1,
        lineHeight: '28.7px',
      },
    },
    '.stamp-tax-declaration-box': {
      display: 'flex',
      flexDirection: 'column',
      width: 183.5,
      // 税務署名が長いと4行になるため、高さは下限にする
      minHeight: 132.4,
      flexShrink: 0,
      // 枠線は実線の hr と同じく背景で描き、黒い背景の上に線の太さだけ内側へ白い背景を重ねる。
      // 影で描くと、html2canvas が紙の下の方にある影を枠の中ごと黒く塗る
      padding: 1.6,
      backgroundColor: '#000000',
      '.stamp-tax-declaration-body': {
        flexGrow: 1,
        padding: 'calc(12.8px - 1.6px)',
        backgroundColor: '#ffffff',
        fontSize: 19.1,
        lineHeight: '35.1px',
        p: {
          letterSpacing: 3.8,
          // 字間は行末の文字の後ろにも付くため、その分だけ右の余白にはみ出させ、Figma と同じ文字数で折り返す
          marginRight: -3.8,
        },
      },
    },
  },
  // 売上票の部品は自分の css で文字の大きさ・余白・点線を決めているため、紙の値で上書きする
  '.sales-slip .sales-slip-block': {
    margin: '0 51.1px',
    fontSize: 19.1,
    lineHeight: '28.7px',
    '.title': { fontWeight: 600 },
  },
  '.payment-front-block': {
    padding: '0 51.1px',
    '.list-item-block': { paddingTop: 12.8 },
  },
  '.guest-app-block': {
    padding: '0 51.1px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '>div:first-of-type': { width: 292 },
    '.title': {
      width: 280.8,
      textAlign: 'center',
      fontSize: 25.5,
      lineHeight: '38.3px',
    },
    hr: { marginTop: 12.8 },
    '.icon-block': {
      display: 'flex',
      // アイコンの間隔 102.1px は、アイコンと文字の欄（幅 76.6px）の中心どうしの距離
      gap: 'calc(102.1px - 76.6px)',
      marginTop: 12.8,
      '.help-icon': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: 76.6,
        'svg, img': { height: 38.3, width: 38.3 },
        // 画像は絵の周りに余白があり、絵が画像の幅に占める割合が画像ごとに違う。絵の幅を Figma の値にするため、
        // 枠の内側の余白を「(枠 38.3px - Figma の絵の幅 / 絵が画像の幅に占める割合) / 2」にする
        '&.-facility img': { padding: 'calc((38.3px - 29.9px / 0.931) / 2)' },
        '&.-travel img': { padding: 'calc((38.3px - 30.5px / 0.977) / 2)' },
        '&.-wifi img': { padding: 'calc((38.3px - 32.8px / 0.996) / 2)' },
        span: { fontSize: 19.1, lineHeight: '28.7px' },
      },
    },
  },
  '.list-item': {
    position: 'relative',
    display: 'grid',
    // 項目名・「:」・値の位置は、どれも行の左端から測る
    gridTemplateColumns: '175.5px 1fr',
    fontSize: 19.1,
    lineHeight: '28.7px',
    '.label': { width: 127.6 },
    '.value::before': {
      content: '":"',
      position: 'absolute',
      left: 140.4,
      // Figma の「:」は、前後に空白を入れた幅 22.3px の欄の中にある
      width: 22.3,
      textAlign: 'center',
    },
  },
  h2: {
    margin: 0,
    textAlign: 'center',
    fontSize: 28.7,
  },
  // 実線は border で描かず、高さと背景で描く。border だと Chrome が太さを整数の画素に丸めて 1.6px が 1px になるため。
  // 点線は、Figma の太さと色（1.6px・#676767）では印刷した紙で薄過ぎるため、Figma の値にせず売上票の部品と同じ指定にする
  'hr, .sales-slip hr': {
    margin: 0,
    border: 0,
    '&.-normal': { height: 1.6, backgroundColor: '#000000' },
    '&.-block': {
      margin: '38.3px 0',
      borderTop: '4px dashed #A3A3A3',
    },
  },
})
